import React, { FunctionComponent, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../../store";
import { Grid, Hidden, Switch, useMediaQuery } from "@material-ui/core";
import BarChart from "../components/BarChart";
import TableWrapper from "../components/TableWrapper";
import moment from "moment";
import { CampaignsStatisticsState } from "../../../store/statistics/campaign/types";
import { thunkFetchCampaignStatistics } from "../../../store/statistics/campaign/actions";
import CampaignProducts from "./CampaignProducts";
import { getCurrencyPositiveOrNegativeColoring } from "../shared/tableFunctions";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { CompareArrows } from "@material-ui/icons";
import { CampaignTargetUserType } from "../../../models/Campaign";
import { useTheme } from "@material-ui/core/styles";
import StatTable from "../components/StatTable";
import { Currency } from "../../../components/misc/Currency";
import { ExclVat } from "../components/exclVat";
import { useI18n } from "../../../hooks/useI18n";

export const CampaignStatistics: FunctionComponent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [campaignDate, setCampaignDate] = useState(moment().toDate());
  const [showGraph, setShowGraph] = useState(false);

  const statisticsState = useSelector<AppState, CampaignsStatisticsState>(
    s => s.campaignStatistics
  );

  let { campaignStatistics, isLoading } = statisticsState;

  useEffect(() => {
    if (campaignDate) dispatch(thunkFetchCampaignStatistics(campaignDate));
  }, [campaignDate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  return (
    <>
      <KeyboardDatePicker
        style={{ marginBottom: 10 }}
        disabled={isLoading}
        onChange={(date: MaterialUiPickersDate) => {
          if (date) setCampaignDate(new Date(date.format("YYYY-MM-DD")));
        }}
        value={campaignDate}
        variant="inline"
        format="YYYY-MM-DD"
      />
      <ExclVat />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={2}
      >
        <Grid item xs={12}>
          <TableWrapper title={t("common.tableHeaders.campaigns")}>
            <Hidden smDown>
              <Grid
                style={{
                  position: "absolute",
                  zIndex: 100,
                  width: "160px",
                  margin: "25px 0 0 10px"
                }}
                component="label"
                container
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                <Grid item>{t("common.messages.table")}</Grid>
                <Grid item>
                  <Switch
                    size={"small"}
                    checked={showGraph}
                    onChange={handleChange}
                    name="graphSwitch"
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item>{t("common.messages.chart")}</Grid>
              </Grid>
            </Hidden>
            {showGraph ? (
              <BarChart
                chartData={{
                  labels: campaignStatistics.map(c => c.campaignName),
                  datasets: [
                    {
                      label: "Sales",
                      rgbColor: { r: 0, g: 148, b: 148 },
                      data: campaignStatistics.map(c => c.sales),
                      stack: "stack 1"
                    },
                    {
                      label: t("common.tableHeaders.acquisitionCost"),
                      rgbColor: { r: 240, g: 155, b: 13 },
                      data: campaignStatistics.map(c => c.cost),
                      stack: "stack 0"
                    },
                    {
                      label: t("common.tableHeaders.campaignDiscount"),
                      rgbColor: { r: 5, g: 89, b: 141 },
                      data: campaignStatistics.map(c => c.campaignDiscount),
                      stack: "stack 0"
                    },
                    {
                      label: "Profit",
                      rgbColor: { r: 31, g: 132, b: 52 },
                      data: campaignStatistics.map(c => c.profit),
                      stack: "stack 0"
                    }
                  ]
                }}
                options={{
                  scales: {
                    x: {
                      stacked: true
                    },
                    y: {
                      stacked: true
                    }
                  }
                }}
                showLegend={true}
                isLoading={isLoading}
              />
            ) : (
              <StatTable
                isLoading={isLoading}
                exportName={t("common.statistics.campaignStatistics")}
                columns={[
                  {
                    title: "Campaign",
                    field: "campaign"
                  },
                  {
                    title: "",
                    field: "compare",
                    hidden: mobile,
                    render: (rowData: any) =>
                      rowData.targetUserType ===
                      CampaignTargetUserType.Stores ? (
                        <CompareArrows />
                      ) : (
                        ""
                      )
                  },
                  {
                    title: "Start",
                    field: "startDate",
                    hidden: mobile,
                    render: (rowData: any) =>
                      moment(rowData.startDate).format("YYYY-MM-DD")
                  },
                  {
                    title: "End",
                    field: "endDate",
                    hidden: mobile,
                    render: (rowData: any) =>
                      moment(rowData.endDate)
                        .subtract(12, "hours")
                        .format("YYYY-MM-DD")
                  },
                  {
                    title: "Sales",
                    field: "sales",
                    hidden: mobile,
                    render: (rowData: any) => <Currency value={rowData.sales} />
                  },
                  {
                    title: t("common.tableHeaders.campaignDiscount"),
                    field: "discount",
                    hidden: mobile,
                    render: (rowData: any) => (
                      <Currency value={rowData.discount} />
                    )
                  },
                  {
                    title: t("common.tableHeaders.acquisitionCost"),
                    field: "cost",
                    hidden: mobile,
                    render: (rowData: any) => <Currency value={rowData.cost} />
                  },
                  {
                    title: "Profit",
                    field: "profit",
                    cellStyle: {
                      borderLeft: "1px solid #e9e5e2",
                      backgroundColor: "rgba(5,89,141, 0.1)"
                    },
                    render: (rowData: any) =>
                      getCurrencyPositiveOrNegativeColoring(rowData.profit)
                  },
                  {
                    title: "Number of purchases",
                    field: "purchases"
                  }
                ]}
                data={
                  !isLoading
                    ? campaignStatistics.map(c => ({
                        campaignId: c.campaignId,
                        campaign: c.campaignName,
                        startDate: c.startDate,
                        endDate: c.endDate,
                        sales: c.sales,
                        discount: c.campaignDiscount,
                        cost: c.cost,
                        profit: c.profit,
                        purchases: c.purchases,
                        targetUserType: c.targetUserType
                      }))
                    : []
                }
                detailPanel={[
                  {
                    render: (rowData: any) => (
                      <CampaignProducts
                        campaignId={rowData.campaignId}
                        targetUserType={rowData.targetUserType}
                        periodFrom={rowData.startDate}
                        periodTo={rowData.endDate}
                      />
                    )
                  }
                ]}
                hideDetailPanel={mobile}
              />
            )}
          </TableWrapper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <BarChart
            title={t("common.tableHeaders.numberOfPurchases")}
            chartData={{
              labels: campaignStatistics.map(c => c.campaignName),
              datasets: [
                {
                  label: "Purchases",
                  data: campaignStatistics.map(c => c.purchases)
                }
              ]
            }}
            showLegend={false}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <BarChart
            title={t("common.tableHeaders.averageReceipt")}
            chartData={{
              labels: campaignStatistics.map(c => c.campaignName),
              datasets: [
                {
                  label: "Average receipt sum",
                  data: campaignStatistics.map(c => c.sales / c.purchases)
                }
              ]
            }}
            showLegend={false}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </>
  );
};
